// src/utils/api.js — Axios instances with JWT auto-refresh
// Access token stored in memory only (never localStorage) to prevent XSS token theft.
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

/** Default request timeout. Override per request with `{ timeout }`. */
export const DEFAULT_TIMEOUT = 15000;
/** For multipart uploads — a 10MB CV on a slow line blows past DEFAULT_TIMEOUT. */
export const UPLOAD_TIMEOUT = 120000;

// ── In-memory token store (not persisted — refresh cookie re-issues on reload) ──
let accessToken = null;

export function getAccessToken() { return accessToken; }
export function setAccessToken(token) { accessToken = token; }
export function clearAccessToken() { accessToken = null; }

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,  // send cookies (refresh token)
  timeout: DEFAULT_TIMEOUT,
});

/**
 * For public / unauthenticated calls (careers page, candidate login & signup,
 * public departments). Deliberately has no auth interceptor and does not send
 * cookies: the refresh cookie's `/api/auth` path covers the candidate-login
 * route, and the admin bearer must never ride along on a candidate request.
 */
export const publicApi = axios.create({
  baseURL: API_BASE,
  withCredentials: false,
  timeout: DEFAULT_TIMEOUT,
});

// Attach access token to every request
api.interceptors.request.use(config => {
  if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`;
  return config;
});

// ── Session-loss notification ───────────────────────────────────────
// Nothing in axios can call setUser(null); AuthProvider registers a handler
// here so a dead session tears down the admin shell instead of leaving it
// rendering cached PII while every request 401s.
let authFailureHandler = null;

/**
 * Register the session-loss handler. Returns an unsubscribe function.
 * @param {(reason: string) => void} handler
 */
export function onAuthFailure(handler) {
  authFailureHandler = typeof handler === 'function' ? handler : null;
  return () => {
    if (authFailureHandler === handler) authFailureHandler = null;
  };
}

/** Clear the token and notify the app that the session is gone. */
export function emitAuthFailure(reason = 'unauthorized') {
  clearAccessToken();
  if (!authFailureHandler) return;
  try {
    authFailureHandler(reason);
  } catch {
    // never let a handler throw out of an interceptor
  }
}

// Single-flight refresh so React 18 StrictMode (and parallel 401s) rotate the
// httpOnly cookie at most once. Callers handle failure — never hard-redirect
// here, or public/guest flows get sent to /admin/login.
let refreshPromise = null;

export function refreshAccessToken() {
  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${API_BASE}/auth/refresh`, {}, { withCredentials: true })
      .then((r) => {
        const token = r.data?.accessToken;
        if (!token) throw new Error('No access token in refresh response');
        setAccessToken(token);
        return token;
      })
      .catch((err) => {
        clearAccessToken();
        throw err;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

// Credential endpoints: a 401 there means "wrong password" / "bad link", not a
// lost session. Refreshing would rotate the cookie for no reason and signing
// the tab out would be wrong. /auth/me is the one authenticated /auth route.
function isCredentialEndpoint(url) {
  const path = String(url || '');
  return /\/auth\//.test(path) && !/\/auth\/me\/?($|\?)/.test(path);
}

api.interceptors.response.use(
  res => res,
  async err => {
    const original = err.config;
    // Any 401 is session loss, not just TOKEN_EXPIRED: an admin deactivated
    // mid-session gets USER_INACTIVE and previously fell through unhandled.
    if (err.response?.status !== 401 || !original || isCredentialEndpoint(original.url)) {
      return Promise.reject(err);
    }
    if (original._retry) {
      emitAuthFailure('unauthorized');
      return Promise.reject(err);
    }
    original._retry = true;
    let newToken;
    try {
      newToken = await refreshAccessToken();
    } catch {
      emitAuthFailure('refresh-failed');
      return Promise.reject(err);
    }
    original.headers = original.headers || {};
    original.headers.Authorization = `Bearer ${newToken}`;
    // A 401 on the retry re-enters this interceptor with `_retry` set and is
    // reported as session loss there.
    return api(original);
  }
);

export default api;
